import { useState, useEffect } from 'react';
import { Grid } from '@chakra-ui/react';
import { sortBy, indexOf } from 'lodash';
import { useAppStore } from '@halo/chat-sdk-react';
import { PinnedItem } from '@halo/ui-chat-react';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { useShallowEnhance } from '@halo/zustand-enhance';
import { SortableItem } from './SortableItem';
import { useChatNavigation } from './helper';

interface PinnedListProps {
  onOrderChange?: (orderedIds: string[]) => void;
}

const PinnedList = ({ onOrderChange }: PinnedListProps) => {
  const { pinnedSessions, client, setPinnedSessions } = useAppStore(
    useShallowEnhance(['pinnedSessions', 'client', 'setPinnedSessions', 'changePinnedList'])
  );
  const [localOrder, setLocalOrder] = useState<string[]>([]);
  const handleActiveSession = useChatNavigation();

  useEffect(() => {
    client?.Api.session.getPinnedSessionChatIds(true).then((res) => {
      setPinnedSessions(res);
      setLocalOrder(res);
    });
  }, [client, setPinnedSessions]);

  useEffect(() => {
    setLocalOrder(pinnedSessions);
  }, [pinnedSessions]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = localOrder.indexOf(active.id as string);
    const newIndex = localOrder.indexOf(over.id as string);
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(localOrder, oldIndex, newIndex);
      setLocalOrder((preState) => sortBy(preState, (item) => indexOf(newOrder, item)));
      onOrderChange?.(newOrder);
      saveOrderToServer(newOrder);
    }
  };

  const saveOrderToServer = async (orderedIds: string[]) => {
    try {
      await client?.Api.session.setPinnedSessionsOrder(orderedIds);
    } catch (error) {
      console.error('Failed to save pinned session order:', error);
    }
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={localOrder} strategy={rectSortingStrategy}>
        <Grid
          templateColumns="repeat(auto-fill, minmax(64px, 1fr))"
          autoRows="1fr"
          px="2"
          rowGap="3"
          columnGap="2"
          pb={localOrder.length ? '3' : '0'}
          justifyItems="center"
        >
          {localOrder.map((item) => {
            return (
              <SortableItem key={item} id={item}>
                <PinnedItem chat_id={item} handleActiveSession={handleActiveSession} />
              </SortableItem>
            );
          })}
        </Grid>
      </SortableContext>
    </DndContext>
  );
};
const MemoPinnedList = memo(PinnedList);
export { MemoPinnedList as PinnedList };
